import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { X, Plus, Trash2, Power, Target as TargetIcon, Save } from 'lucide-react';
import { targetAPI } from '../api';
import { getTargetMonthRange, localDateBoundaryIso } from '../lib/dateRanges';

export const TARGET_TYPE_OPTIONS = [
  { v: 'sales', l: 'Sales (Deals Closed)' },
  { v: 'revenue', l: 'Revenue (₹)' },
  { v: 'calls', l: 'Calls' },
  { v: 'connected_calls', l: 'Connected Calls' },
  { v: 'talk_time_minutes', l: 'Talk Time (min)' },
  { v: 'follow_ups', l: 'Follow-ups' },
  { v: 'meetings', l: 'Meetings' },
  { v: 'site_visits', l: 'Site Visits' },
  { v: 'quotations', l: 'Quotations' },
  { v: 'conversion_rate', l: 'Conversion Rate (%)' },
];
const PERIODS = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
const typeLabel = (v) => TARGET_TYPE_OPTIONS.find((t) => t.v === v)?.l || v;
const fmtVal = (t, v) => (v == null ? '—'
  : t === 'revenue' ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
    : t === 'conversion_rate' ? `${v}%` : Number(v).toLocaleString('en-IN'));
const day = (v) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—');

const field = 'w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200';

export const TeamTargetsDrawer = ({ open, onClose, team = [], canManage, onChanged }) => {
  const range = getTargetMonthRange();
  const [uid, setUid] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [form, setForm] = useState({
    period_type: 'monthly', target_type: 'sales', target_value: 10,
    period_start: range.start, period_end: range.end,
  });

  useEffect(() => { if (open && !uid && team.length) setUid(team[0].id); }, [open, team, uid]);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await targetAPI.list(id);
      setRows(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load targets');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open && uid) load(uid); }, [open, uid, load]);

  const after = (msg) => { toast.success(msg); load(uid); onChanged?.(); };

  const create = async (e) => {
    e.preventDefault();
    try {
      await targetAPI.create({
        user_id: uid, period_type: form.period_type, target_type: form.target_type,
        target_value: parseFloat(form.target_value),
        period_start: localDateBoundaryIso(form.period_start),
        period_end: localDateBoundaryIso(form.period_end, true),
        is_active: true,
      });
      setAdding(false);
      after('Target assigned');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not create target'); }
  };

  const saveValue = async (id) => {
    try {
      await targetAPI.update(id, { target_value: parseFloat(editValue) });
      setEditId(null);
      after('Target updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not update'); }
  };

  const toggle = async (row) => {
    try {
      await targetAPI.update(row._id || row.id, { is_active: !(row.is_active !== false) });
      after(row.is_active !== false ? 'Target deactivated' : 'Target activated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not change status'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this target?')) return;
    try { await targetAPI.delete(id); after('Target deleted'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not delete'); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="sm-targets-drawer">
      <button aria-label="Close" onClick={onClose}
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" data-testid="sm-targets-backdrop" />
      <aside className="relative flex h-full w-full max-w-[520px] flex-col overflow-y-auto border-l border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-800 dark:bg-slate-950">
        <header className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 font-outfit text-lg font-bold text-slate-900 dark:text-white">
              <TargetIcon className="h-[18px] w-[18px] text-emerald-600" />Target Management
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">Achievement is always computed from live CRM activity.</p>
          </div>
          <button onClick={onClose} data-testid="sm-targets-close"
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
        </header>

        <label className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Team Member</label>
        <select value={uid} onChange={(e) => { setUid(e.target.value); setAdding(false); }}
          data-testid="sm-targets-user-select" className={`mt-1 ${field}`}>
          {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        {canManage && (
          <button onClick={() => setAdding((a) => !a)} data-testid="sm-add-target-btn"
            className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-xl bg-[#0E1626] px-3.5 py-2 text-xs font-bold text-white transition-transform hover:-translate-y-0.5">
            <Plus className="h-3.5 w-3.5" />Add Target
          </button>
        )}

        {adding && canManage && (
          <form onSubmit={create} className="mt-3 grid grid-cols-2 gap-2.5 rounded-xl border border-slate-200 bg-slate-50/70 p-3 dark:border-slate-800 dark:bg-slate-900/40"
            data-testid="sm-target-form">
            <label className="col-span-1 text-[11px] font-semibold text-slate-500">Target Type
              <select value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value })}
                data-testid="sm-target-type" className={`mt-1 ${field}`}>
                {TARGET_TYPE_OPTIONS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </label>
            <label className="text-[11px] font-semibold text-slate-500">Period
              <select value={form.period_type} onChange={(e) => setForm({ ...form, period_type: e.target.value })}
                data-testid="sm-target-period" className={`mt-1 ${field}`}>
                {PERIODS.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
              </select>
            </label>
            <label className="text-[11px] font-semibold text-slate-500">Start
              <input type="date" required value={form.period_start} data-testid="sm-target-start"
                onChange={(e) => setForm({ ...form, period_start: e.target.value })} className={`mt-1 ${field}`} />
            </label>
            <label className="text-[11px] font-semibold text-slate-500">End
              <input type="date" required value={form.period_end} data-testid="sm-target-end"
                onChange={(e) => setForm({ ...form, period_end: e.target.value })} className={`mt-1 ${field}`} />
            </label>
            <label className="text-[11px] font-semibold text-slate-500">Target Value
              <input type="number" required min="0" step="any" value={form.target_value} data-testid="sm-target-value"
                max={form.target_type === 'conversion_rate' ? 100 : undefined}
                onChange={(e) => setForm({ ...form, target_value: e.target.value })} className={`mt-1 ${field}`} />
            </label>
            <div className="col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setAdding(false)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 dark:border-slate-700">Cancel</button>
              <button type="submit" data-testid="sm-target-save"
                className="rounded-lg bg-emerald-600 px-3.5 py-1.5 text-xs font-bold text-white">Save Target</button>
            </div>
          </form>
        )}

        <ul className="mt-4 space-y-2.5" data-testid="sm-targets-list">
          {loading && <li className="py-6 text-center text-xs text-slate-400">Loading targets…</li>}
          {!loading && !rows.length && (
            <li className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400 dark:border-slate-800"
              data-testid="sm-targets-empty">No targets assigned yet.</li>
          )}
          {!loading && rows.map((r) => {
            const id = r._id || r.id;
            const active = r.is_active !== false;
            return (
              <li key={id} data-testid={`sm-target-row-${id}`}
                className={`rounded-xl border p-3 ${active ? 'border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/40' : 'border-slate-200 bg-slate-50 opacity-70 dark:border-slate-800 dark:bg-slate-900/20'}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-800 dark:text-slate-100">{typeLabel(r.target_type)}</p>
                    <p className="mt-0.5 text-[11px] font-medium capitalize text-slate-500">
                      {r.period_type} · {day(r.period_start)} → {day(r.period_end)}
                      {!active && <span className="ml-1.5 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold text-slate-600">Inactive</span>}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {editId === id ? (
                      <>
                        <input type="number" min="0" step="any" value={editValue} data-testid={`sm-target-edit-input-${id}`}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-24 rounded-lg border border-slate-200 px-2 py-1 text-xs font-bold dark:border-slate-700 dark:bg-slate-900" />
                        <button onClick={() => saveValue(id)} data-testid={`sm-target-edit-save-${id}`}
                          className="rounded-lg bg-emerald-600 p-1.5 text-white"><Save className="h-3.5 w-3.5" /></button>
                      </>
                    ) : (
                      <span className="font-outfit text-sm font-extrabold text-slate-900 dark:text-white"
                        data-testid={`sm-target-value-${id}`}>{fmtVal(r.target_type, r.target_value)}</span>
                    )}
                  </div>
                </div>
                {canManage && (
                  <div className="mt-2.5 flex items-center gap-2">
                    <button onClick={() => { setEditId(id); setEditValue(String(r.target_value ?? '')); }}
                      data-testid={`sm-target-edit-${id}`}
                      className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300">Edit</button>
                    <button onClick={() => toggle(r)} data-testid={`sm-target-toggle-${id}`}
                      className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-bold ${active
                        ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                      <Power className="h-3 w-3" />{active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button onClick={() => remove(id)} data-testid={`sm-target-delete-${id}`}
                      className="ml-auto rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </aside>
    </div>
  );
};

export default TeamTargetsDrawer;
